// Clipboard helper — CHR-06 §6. `navigator.clipboard.writeText` is the primary
// path (the deployment is HTTPS, so the API is available), with the legacy
// hidden-`textarea` + `execCommand('copy')` fallback for older Safari and for
// any context that refuses clipboard permission.
//
// NEVER throws: a copy that could not happen is reported as `false` so the
// caller can show a failure toast instead of an unhandled rejection.

export const copyText = async (text: string): Promise<boolean> => {
  const clipboard = navigator.clipboard as Clipboard | undefined;
  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied / non-secure context — fall through to the legacy
      // path rather than failing the user's click outright.
    }
  }
  return copyViaTextarea(text);
};

const copyViaTextarea = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Off-screen but still focusable/selectable — `display:none` would make the
  // selection (and therefore the copy) a no-op.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    // The scratch node must go even when the copy threw.
    document.body.removeChild(textarea);
  }
};
